
import type {ComponentProps, ComponentType, ReactNode} from 'react';
import React, {useEffect, forwardRef, useState} from 'react';
import {withCssModule} from '@pb/kit-util-with-css-module';
import styles from './input.component.module.styl';
import {convertPxToRem} from '@pb/kit-util-convert-px-to-rem';
import {useAdornmentWidth} from './utils/use-adornment-width';
import {useCustomGroupItemContext} from './custom-group/custom-group-item-context';

import type {CustomInputProps} from './suffixed-input/suffixed-input.component';

const classNames = withCssModule(styles);

const getNativeProps = (props: InputProps): NativeProps => {
    // eslint-disable-next-line @stylistic/max-len
    const {rootRef, unstyledReadonly, inputComponent, rootProps, color, startAdornment, startAdornmentProps, endAdornment, endAdornmentProps, ...nativeProps} = props;
    return nativeProps;
};

type NativeProps = Omit<ComponentProps<'input'>, 'ref'>;

const TYPE_CLASSES = {
    SUCCESS: 'input__root_success',
    ERROR: 'input__root_error',
    DEFAULT: '',
};

export interface InputProps extends NativeProps {
    /**
     * ref to root element
     * */
    rootRef?: React.Ref<HTMLDivElement>,
    /**
     * props for root element
     * */
    rootProps?: ComponentProps<'div'>,
    /**
     * color state of input
     * */
    color?: 'SUCCESS' | 'ERROR' | 'DEFAULT',
    /**
     * element to render before input
     * */
    startAdornment?: ReactNode,
    /**
     * props for startAdornment
     * */
    startAdornmentProps?: ComponentProps<'div'>,
    /**
     * element to render after input
     * */
    endAdornment?: ReactNode,
    /**
     * props for endAdornment
     * */
    endAdornmentProps?: ComponentProps<'div'>,
    /**
     * custom component for input
     * */
    inputComponent?: ComponentType<CustomInputProps>,

    /**
     * If true, read-only styles will not be applied for read-only state.
     */
    unstyledReadonly?: boolean,
}

/**
 * @description Main text field component.
 *
 * ## Features
 *
 * Extends native input.
 * Has a div wrapper.
 * Can be integrated with 3rd party tools
 * See properties (controls) for more details.
 * TypedInput component - input with validation.
 * SuffixedInput component - input with suffix in value.
 * DataTypedInput component - input with predefined data type and Locale.
 *
 * ### Accessibility
 *
 * It accepts all native input attributes that are available in HTML. Please follow standard requirement for accessibility, e.g. https://www.w3.org/WAI/tutorials/forms/instructions/.
 * - Use `aria-label` or `aria-labelledby` to provide a label for the input.
 * - Use `aria-describedby` to provide a description for the input.
 *
 * ## Migration to V2
 *
 * The `@pb/kit-component-input`, `@pb/kit-component-data-typed-input` are deprecated and will be removed in the future. The new version of the component is `@pb/kit-component-input-v2`. The main changes are:
 *
 * Use startAdornment, endAdornment instead of iconBefore, iconAfter, anchorBefore, anchorAfter and their props, clearableValue, clearable.
 * Suffix is moved to SuffixedInput.
 * Use rootProps instead of clickOnDisabled, width, height.
 * ClearableValue is removed, set value instead.
 * Suggestions are removed, use Autocomplete instead.
 * Validation, message is moved to TypedInput.
 * ShowEllipsisHint is removed, use @pb/kit-util-show-tooltip.
 * TextAlign is removed, use className="text-align..." instead.
 * TypedInput contains validation, formatting, error message from old \`@pb/kit-component-input\` Input component.
 * DataTypedInput is copied from @pb/kit-component-data-typed-input.
 * DataTypedInput - tooltip is removed, compose with Tooltip outside.
 * DataTypedInput - autoComplete is removed, use Autocomplete instead.
 * DataTypedInput - input props are merged with rest of props.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-input}
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
    const {
        rootRef,
        rootProps,
        color = 'DEFAULT',
        startAdornment,
        startAdornmentProps,
        endAdornment,
        endAdornmentProps,
        unstyledReadonly,
        inputComponent: InputComponent = 'input',
    } = props;

    const {inputId} = useCustomGroupItemContext();
    const nativeProps = getNativeProps(props);
    const [value, setValue] = useState<string | number | readonly string[] | undefined>(nativeProps.value ?? '');
    useEffect(() => {
        setValue(nativeProps.value);
    }, [nativeProps.value]);
    const [isActive, setActive] = useState<boolean>();
    const [leftAdornmentWidth, leftAdornmentRef] = useAdornmentWidth();
    const [rightAdornmentWidth, rightAdornmentRef] = useAdornmentWidth();

    return (
        <div
            ref={rootRef}
            {...rootProps}
            className={classNames('input__root', rootProps?.className, TYPE_CLASSES[color], {
                input__root_populated: !!value,
                input__root_uneditable: nativeProps.readOnly && !unstyledReadonly,
                input__root_disabled: nativeProps.disabled,
                input__root_active: isActive,
            })}>
            {startAdornment ? (
                <div
                    {...startAdornmentProps}
                    ref={leftAdornmentRef}
                    className={classNames('input__adornment', 'input__adornment_left', startAdornmentProps?.className)}>
                    {startAdornment}
                </div>
            ) : null}
            <InputComponent
                autoComplete="off"
                {...nativeProps}
                id={nativeProps?.id || inputId}
                className={classNames('input__component', nativeProps.className)}
                aria-invalid={nativeProps['aria-invalid'] ?? color === 'ERROR'}
                ref={ref}
                onFocus={e => {
                    setActive(true);
                    nativeProps.onFocus?.(e);
                }}
                onBlur={e => {
                    setActive(false);
                    nativeProps.onBlur?.(e);
                }}
                onChange={e => {
                    setValue(e.target.value);
                    nativeProps.onChange?.(e);
                }}
                style={{
                    ...nativeProps.style,
                    paddingLeft: leftAdornmentWidth ? convertPxToRem(leftAdornmentWidth + 8) : undefined,
                    paddingRight: rightAdornmentWidth ? convertPxToRem(rightAdornmentWidth + 8) : undefined,
                }}
            />
            {endAdornment ? (
                <div
                    {...endAdornmentProps}
                    ref={rightAdornmentRef}
                    className={classNames('input__adornment', 'input__adornment_right', endAdornmentProps?.className)}>
                    {endAdornment}
                </div>
            ) : null}
        </div>
    );
});

Input.displayName = 'Input';
